import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { useOsmForwardGeocode } from '@/api/hooks/useOsmGeocode';
import { useOsmOptimized, useOsmRoute } from '@/api/hooks/useOsmRoute';
import type { Costing, RouteResponse } from '@/types/osm';

export interface RouteStop {
  id: string;
  label: string;
  lng: number | null;
  lat: number | null;
}

interface RoutePanelProps {
  stops: RouteStop[];
  setStops: (s: RouteStop[]) => void;
  pendingClick: { lng: number; lat: number } | null;
  consumePendingClick: () => void;
  onResult: (resp: RouteResponse, optimized: boolean) => void;
  onClear: () => void;
}

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 });

function newStop(label = ''): RouteStop {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, label, lng: null, lat: null };
}

export default function RoutePanel({
  stops,
  setStops,
  pendingClick,
  consumePendingClick,
  onResult,
  onClear,
}: RoutePanelProps) {
  const [costing, setCosting] = useState<Costing>('auto');
  const [optimize, setOptimize] = useState(true);
  const [shortest, setShortest] = useState(false);
  const [noTolls, setNoTolls] = useState(false);
  const geocode = useOsmForwardGeocode();
  const routeMut = useOsmRoute();
  const optMut = useOsmOptimized();
  const [activeStopIdx, setActiveStopIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!pendingClick || activeStopIdx == null) return;
    const idx = activeStopIdx;
    const next = stops.slice();
    next[idx] = {
      ...next[idx],
      lng: pendingClick.lng,
      lat: pendingClick.lat,
      label: next[idx].label || `${pendingClick.lat.toFixed(4)}, ${pendingClick.lng.toFixed(4)}`,
    };
    setStops(next);
    consumePendingClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingClick]);

  function update(i: number, patch: Partial<RouteStop>) {
    const next = stops.slice();
    next[i] = { ...next[i], ...patch };
    setStops(next);
  }

  async function resolveStop(i: number) {
    const s = stops[i];
    if (!s.label.trim() || (s.lng != null && s.lat != null)) return;
    try {
      const r = await geocode.mutateAsync(s.label);
      if (r.length > 0) {
        const top = r[0];
        const lat = typeof top.lat === 'string' ? parseFloat(top.lat) : top.lat;
        const lon = typeof top.lon === 'string' ? parseFloat(top.lon) : top.lon;
        update(i, { lng: lon, lat, label: top.display_name });
      }
    } catch {
      /* ignore — search may be unavailable */
    }
  }

  async function compute() {
    const ready = stops.filter((s) => s.lng != null && s.lat != null);
    if (ready.length < 2) return;
    const useTSP = optimize && ready.length >= 3;
    const opts: Record<string, unknown> = {};
    if (noTolls) opts.use_tolls = 0;
    if (shortest) opts.shortest = true;
    const body = {
      locations: ready.map((s, i) => ({
        lon: s.lng!,
        lat: s.lat!,
        type: (i === 0 || i === ready.length - 1 ? 'break' : 'through') as 'break' | 'through',
      })),
      costing,
      costing_options: { [costing]: opts },
      directions_options: { units: 'kilometers' as const },
    };
    try {
      const resp = useTSP ? await optMut.mutateAsync(body) : await routeMut.mutateAsync(body);
      onResult(resp, useTSP);
    } catch {
      /* error rendered below */
    }
  }

  const ready = stops.filter((s) => s.lng != null && s.lat != null).length;
  const error = (routeMut.error as Error | null) ?? (optMut.error as Error | null);
  const pending = routeMut.isPending || optMut.isPending;
  const trip = (routeMut.data ?? optMut.data)?.trip;

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Multi-stop routing</h3>
        <p className="text-xs text-muted-foreground">
          Type a place and press Enter, or click on the map to drop a stop.
        </p>
      </div>

      <div className="space-y-2">
        {stops.map((s, i) => (
          <div key={s.id} className="flex items-start gap-1">
            <div
              className={`mt-1.5 h-5 w-5 shrink-0 rounded-full text-center text-[11px] font-semibold leading-5 ${
                activeStopIdx === i ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
              }`}
            >
              {i + 1}
            </div>
            <div className="flex-1">
              <Input
                value={s.label}
                placeholder={`Stop ${i + 1}`}
                onFocus={() => setActiveStopIdx(i)}
                onChange={(e) => update(i, { label: e.target.value, lng: null, lat: null })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void resolveStop(i);
                  }
                }}
                className="h-8 text-xs"
              />
              {s.lng != null && s.lat != null && (
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {s.lat.toFixed(4)}, {s.lng.toFixed(4)}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                const next = stops.slice();
                next.splice(i, 1);
                setStops(next.length ? next : [newStop(), newStop()]);
              }}
              className="mt-1 text-muted-foreground hover:text-red-600"
              title="Remove stop"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setStops([...stops, newStop()])}
          className="h-7 w-full text-xs"
        >
          <Plus className="mr-1 h-3 w-3" /> Add stop
        </Button>
      </div>

      <div className="space-y-2 rounded-md border p-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Mode</span>
          <Select value={costing} onValueChange={(v) => setCosting(v as Costing)}>
            <SelectTrigger className="h-7 flex-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Car</SelectItem>
              <SelectItem value="truck">Truck</SelectItem>
              <SelectItem value="bicycle">Bicycle</SelectItem>
              <SelectItem value="pedestrian">Walk</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={optimize} onChange={(e) => setOptimize(e.target.checked)} />
          Optimize order (TSP, ≥3 stops)
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={shortest} onChange={(e) => setShortest(e.target.checked)} />
          Shortest distance (vs fastest)
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={noTolls} onChange={(e) => setNoTolls(e.target.checked)} />
          Avoid tolls
        </label>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          className="flex-1"
          disabled={ready < 2 || pending}
          onClick={() => void compute()}
        >
          {pending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1 h-3.5 w-3.5" />}
          Route ({ready} stops)
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setStops([newStop(), newStop()]);
            routeMut.reset();
            optMut.reset();
            onClear();
          }}
        >
          Clear
        </Button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-2 text-xs text-red-800">{error.message}</CardContent>
        </Card>
      )}

      {trip && (
        <Card>
          <CardContent className="space-y-2 p-3 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Distance</span>
              <span className="font-semibold">{NUM.format(trip.summary.length)} km</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Time</span>
              <span className="font-semibold">{NUM.format(trip.summary.time / 60)} min</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Legs</span>
              <span>{trip.legs.length}</span>
            </div>
            {trip.locations && optimize && (
              <div className="border-t pt-1">
                <div className="text-muted-foreground">Optimised order</div>
                <div className="font-mono">
                  {trip.locations.map((l) => (l.original_index ?? 0) + 1).join(' → ')}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
